/* FASE 13 (WS4) — Página corporativa "Features": recorrido real de las
   funcionalidades de la plataforma; cada bloque enlaza a su intención. */
import {
  CreditCard, FileCheck, Gavel, Globe, Languages, Radio, Stethoscope, User,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { H_SECTION, PrimaryCta, SUBLINE } from "@/components/marketing/Sections";
import { BRAND } from "@/config/brand";
import { useLocalePath } from "@/lib/locale";
import { PUBLIC_BASE, useJsonLd, usePageMeta } from "@/lib/seo";

/* Cada funcionalidad enlaza a la página que resuelve su intención de búsqueda. */
const FEATURES = [
  { n: 1, icon: Globe, to: "/for-organisers" },
  { n: 2, icon: Gavel, to: "/for-buyers" },
  { n: 3, icon: Radio, to: "/how-it-works" },
  { n: 4, icon: User, to: "/auctions" },
  { n: 5, icon: Stethoscope, to: "/for-buyers" },
  { n: 6, icon: CreditCard, to: "/pricing" },
  { n: 7, icon: FileCheck, to: "/legal/terms" },
  { n: 8, icon: Languages, to: "/about" },
];

export default function Features() {
  const { t } = useTranslation();
  const lp = useLocalePath();
  usePageMeta({
    title: `${t("ft.eyebrow")} — ${BRAND.name}`,
    description: t("ft.sub"),
    localized: true,
  });
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `${t("ft.eyebrow")} — ${BRAND.name}`,
    description: t("ft.sub"),
    itemListElement: FEATURES.map(({ n }, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: t(`ft.f${n}t`),
      description: t(`ft.f${n}d`),
    })),
  });

  return (
    <div data-testid="features-page">
      {/* Hero */}
      <section className="max-w-[980px] mx-auto px-6 pt-20 pb-16 text-center flex flex-col items-center gap-5">
        <p className="text-sm font-semibold tracking-[0.12em] uppercase text-[#6e6e73]">{t("ft.eyebrow")}</p>
        <h1 className={`${H_SECTION} text-[#1d1d1f]`} data-testid="ft-title">{t("ft.title")}</h1>
        <p className={`${SUBLINE} text-[#4b4b50] max-w-2xl`}>{t("ft.sub")}</p>
      </section>

      {/* Grid de funcionalidades reales */}
      <section className="bg-[#f5f5f7]">
        <div className="max-w-[980px] mx-auto px-6 py-20 grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-12">
          {FEATURES.map(({ n, icon: Icon, to }) => (
            <div key={n} className="flex flex-col gap-3" data-testid={`ft-block-${n}`}>
              <Icon className="w-6 h-6 text-[#1d1d1f]" strokeWidth={1.5} aria-hidden="true" />
              <h2 className="text-[17px] font-semibold text-[#1d1d1f]">{t(`ft.f${n}t`)}</h2>
              <p className="text-sm text-[#3f3f44] leading-relaxed">{t(`ft.f${n}d`)}</p>
              <Link to={lp(to)} data-testid={`ft-link-${n}`} className="text-sm font-semibold text-[#1d4d3b] hover:underline w-fit">
                {t("ft.linkLabel")} →
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* CTA final */}
      <section className="max-w-[780px] mx-auto px-6 py-20 text-center flex flex-col items-center gap-6">
        <h2 className="text-2xl lg:text-3xl font-semibold text-[#1d1d1f]">{t("ft.ctaTitle")}</h2>
        <PrimaryCta small to="/how-it-works" testid="ft-final-cta">{t("mk.learnMore")}</PrimaryCta>
      </section>
    </div>
  );
}
